import React from 'react';
import { EdgeProps } from '@xyflow/react';

const BASE_OFFSET = 40;
const BASE_STEP = 8;
const BASE_CURVE = 4;

const CONSANGUINEOUS_SPACING = 1.5;
const CONSANGUINEOUS_STYLE = {
  strokeWidth: 2,
};

const SEPARATED_GAP = 6;
const SEPARATED_DIAGONAL_LENGTH = 16;

// -----------------------------------------------------------------------------
interface SpouseToRelationshipEdgeProps extends EdgeProps {
  data?: {
    customPath?: string;
    offsetValue?: { index: number; total: number };
    relationshipType?: 'partnership' | 'separated';
    isConsanguineous?: boolean;
  };
}

// -----------------------------------------------------------------------------
const SpouseToRelationshipEdge: React.FC<SpouseToRelationshipEdgeProps> = ({
  id,
  sourceX,
  sourceY,
  targetX,
  targetY,
  style = {},
  data,
  markerEnd,
}) => {
  const isDifferentGeneration = Math.abs(sourceY - targetY) > 1;
  const isConsanguineous = data?.isConsanguineous || false;
  const isSeparated = data?.relationshipType === 'separated';

  // ---------------------------------------------------------------------------
  if (!data?.offsetValue && !isDifferentGeneration) {
    const path = isConsanguineous
      ? getRelationshipStraightPath({
          sourceX,
          sourceY: sourceY - CONSANGUINEOUS_SPACING,
          targetX,
          targetY: targetY - CONSANGUINEOUS_SPACING,
          isSeparated,
          diagonalsOffset: -CONSANGUINEOUS_SPACING,
        }) +
        getRelationshipStraightPath({
          sourceX,
          sourceY: sourceY + CONSANGUINEOUS_SPACING,
          targetX,
          targetY: targetY + CONSANGUINEOUS_SPACING,
          isSeparated,
          diagonalsOffset: CONSANGUINEOUS_SPACING,
        })
      : getRelationshipStraightPath({
          sourceX,
          sourceY: sourceY,
          targetX,
          targetY: targetY,
          isSeparated,
        });

    return (
      <path
        id={id}
        style={{ ...style, ...(isConsanguineous ? CONSANGUINEOUS_STYLE : {}) }}
        className="react-flow__edge-path"
        d={path}
        markerEnd={markerEnd}
      />
    );
  }

  // ---------------------------------------------------------------------------
  const { index: offsetIndex, total: offsetTotal } = data?.offsetValue || {
    index: 1,
    total: 1,
  };

  // ---------------------------------------------------------------------------
  const edgePath = isConsanguineous
    ? getOffsetPath({
        offsetIndex,
        offsetTotal,
        sourceY: sourceY - CONSANGUINEOUS_SPACING,
        targetX,
        sourceX,
        targetY: targetY - CONSANGUINEOUS_SPACING,
        isSeparated,
        diagonalsOffset: +CONSANGUINEOUS_SPACING,
        isDifferentGeneration,
      }) +
      getOffsetPath({
        offsetIndex,
        offsetTotal,
        sourceY: sourceY + CONSANGUINEOUS_SPACING,
        targetX,
        sourceX,
        targetY: targetY + CONSANGUINEOUS_SPACING,
        isSeparated,
        diagonalsOffset: -CONSANGUINEOUS_SPACING,
        isDifferentGeneration,
      })
    : getOffsetPath({
        offsetIndex,
        offsetTotal,
        sourceY,
        targetX,
        sourceX,
        targetY,
        isSeparated,
        isDifferentGeneration,
      });

  // ---------------------------------------------------------------------------
  return (
    <path
      id={id}
      style={{ ...style, ...(isConsanguineous ? CONSANGUINEOUS_STYLE : {}) }}
      className="react-flow__edge-path"
      d={edgePath}
      markerEnd={markerEnd}
    />
  );
};

export default SpouseToRelationshipEdge;

// -----------------------------------------------------------------------------
const getOffsetPath = (data: {
  offsetIndex: number;
  offsetTotal: number;
  sourceY: number;
  targetX: number;
  sourceX: number;
  targetY: number;
  isSeparated?: boolean;
  diagonalsOffset?: number;
  isDifferentGeneration?: boolean;
}) => {
  const {
    offsetIndex,
    offsetTotal,
    sourceY,
    targetX,
    sourceX,
    targetY,
    isSeparated = false,
    diagonalsOffset = 0,
    isDifferentGeneration = false,
  } = data;

  const yOffsetStep = BASE_STEP * offsetIndex;
  const verticalOffset = BASE_OFFSET + yOffsetStep;

  const xOffsetStep = BASE_STEP * (offsetTotal - offsetIndex + 1);
  const yAdjusted = isDifferentGeneration ? sourceY + verticalOffset : sourceY - verticalOffset;
  const direction = targetX > sourceX ? 1 : -1;
  const curve = direction * BASE_CURVE;
  const sourceXAdjusted = sourceX + direction * xOffsetStep;
  const targetXAdjusted = targetX - direction * xOffsetStep;

  if (!isSeparated) {
    // Create a smooth cubic Bézier curve for the 4 direction change points
    const edgePath = `
    M ${sourceX},${sourceY}
    L ${sourceXAdjusted - curve},${sourceY}
    Q ${sourceXAdjusted},${sourceY} ${sourceXAdjusted},${isDifferentGeneration ? sourceY + BASE_CURVE : sourceY - BASE_CURVE}
    L ${sourceXAdjusted},${ isDifferentGeneration ? yAdjusted - BASE_CURVE : yAdjusted + BASE_CURVE}
    Q ${sourceXAdjusted},${yAdjusted} ${sourceXAdjusted + curve},${yAdjusted}
    L ${targetXAdjusted - curve},${yAdjusted}
    Q ${targetXAdjusted},${yAdjusted} ${targetXAdjusted},${
      yAdjusted + BASE_CURVE
    }
    L ${targetXAdjusted},${targetY - BASE_CURVE}
    Q ${targetXAdjusted},${targetY} ${targetXAdjusted + curve},${targetY}
    L ${targetX},${targetY}
    `;
    return edgePath;
  }

  // For separated relationships, add diagonals before the final bezier curve
  const diagonalsX = targetXAdjusted - curve * 4; // Adjusted X for diagonals
  const diagonalsY = yAdjusted + diagonalsOffset;

  const diagonalsXMinusGap = diagonalsX - (direction * SEPARATED_GAP) / 2;
  const diagonalsXPlusGap = diagonalsX + (direction * SEPARATED_GAP) / 2;

  const diagonalsPath = getRelationshipDiagonalsPath({
    sourceX: diagonalsXMinusGap,
    targetX: diagonalsXPlusGap,
    y: diagonalsY,
  });

  // Create path with diagonals near the target end
  const edgePath = `
  M ${sourceX},${sourceY}
  L ${sourceXAdjusted - curve},${sourceY}
    Q ${sourceXAdjusted},${sourceY} ${sourceXAdjusted},${isDifferentGeneration ? sourceY + BASE_CURVE : sourceY - BASE_CURVE}
    L ${sourceXAdjusted},${ isDifferentGeneration ? yAdjusted - BASE_CURVE : yAdjusted + BASE_CURVE}
  Q ${sourceXAdjusted},${yAdjusted} ${sourceXAdjusted + curve},${yAdjusted}
  L ${diagonalsXMinusGap},${yAdjusted}

  ${diagonalsPath}

  M ${diagonalsXPlusGap},${yAdjusted}
  L ${targetXAdjusted - curve},${yAdjusted}
  Q ${targetXAdjusted},${yAdjusted} ${targetXAdjusted},${yAdjusted + BASE_CURVE}
  L ${targetXAdjusted},${targetY - BASE_CURVE}
  Q ${targetXAdjusted},${targetY} ${targetXAdjusted + curve},${targetY}
  L ${targetX},${targetY}
  `;
  return edgePath;
};

// -----------------------------------------------------------------------------
const getRelationshipStraightPath = (data: {
  sourceX: number;
  sourceY: number;
  targetX: number;
  targetY: number;
  isSeparated: boolean;
  diagonalsOffset?: number;
}) => {
  const {
    sourceX,
    sourceY,
    targetX,
    targetY,
    isSeparated,
    diagonalsOffset = 0,
  } = data;
  if (!isSeparated) {
    return `
  M ${sourceX},${sourceY}
  L ${targetX},${targetY}
  `;
  }

  const direction = targetX > sourceX ? 1 : -1;
  const deltaX = Math.abs(targetX - sourceX);
  const deltaY = Math.abs(targetY - sourceY);
  const oneThirdX = deltaX / 3 + diagonalsOffset * direction;
  const oneThirdY = deltaY / 3;

  const thirdFromEndX = targetX - direction * oneThirdX;
  const thirdFromEndXPlusGap = thirdFromEndX + (direction * SEPARATED_GAP) / 2;
  const thirdFromEndXMinusGap = thirdFromEndX - (direction * SEPARATED_GAP) / 2;
  const thirdFromEndY = targetY - (targetY > sourceY ? oneThirdY : -oneThirdY);

  const diagonalsPath = getRelationshipDiagonalsPath({
    sourceX: thirdFromEndXMinusGap,
    targetX: thirdFromEndXPlusGap,
    y: thirdFromEndY,
  });

  return `
  M ${sourceX},${sourceY}
  L ${thirdFromEndXMinusGap},${thirdFromEndY}

  ${diagonalsPath}

  M ${thirdFromEndXPlusGap},${thirdFromEndY}
  L ${targetX},${targetY}
  `;
};

// -----------------------------------------------------------------------------
const getRelationshipDiagonalsPath = (data: {
  sourceX: number;
  targetX: number;
  y: number;
}) => {
  const { sourceX, targetX, y } = data;

  const diagonalAxialComponent = SEPARATED_DIAGONAL_LENGTH / 2 / Math.sqrt(2);

  const diagonalAlphaStartX = sourceX + diagonalAxialComponent;
  const diagonalAlphaStartY = y - diagonalAxialComponent;
  const diagonalAlphaEndX = sourceX - diagonalAxialComponent;
  const diagonalAlphaEndY = y + diagonalAxialComponent;

  const diagonalBetaStartX = targetX + diagonalAxialComponent;
  const diagonalBetaStartY = y - diagonalAxialComponent;
  const diagonalBetaEndX = targetX - diagonalAxialComponent;
  const diagonalBetaEndY = y + diagonalAxialComponent;

  return `
  M ${diagonalAlphaStartX},${diagonalAlphaStartY}
  L ${diagonalAlphaEndX},${diagonalAlphaEndY}

  M ${diagonalBetaStartX},${diagonalBetaStartY}
  L ${diagonalBetaEndX},${diagonalBetaEndY}
    `;
};
